/**
 * Таблица строк подраздела «Экологии» (ecology-sites, вариант Б): «Проектные работы»
 * (`section="projects"`, документы группами по площадкам), «Отчётность» (`section="reports"`) и
 * «Лабораторный контроль» (`section="lab"`, ecology-lab-control) — единым списком по сроку. Подраздел
 * строки задаёт её позиция справочника; вид строки (документ или отчётность) следует из подраздела. Над таблицей — фильтры по цвету светофора со счётчиками, выбор площадки и
 * кнопка внесения; в таблице — группа на площадку (категория, пустое состояние), строки со статусом
 * от сервера, происхождением и действиями: правка, скан (прикрепить / скачать / убрать), «убрать»
 * строку.
 *
 * Строку, выполненную Центрикой по заказу, клиент не правит и не убирает (кнопок нет, сервер тоже
 * отказывает); команда (`byTeam`) правит любые строки — такую строку клиент открывает только для
 * просмотра.
 *
 * Клик по строке открывает её карточку, а группы площадок сворачиваются (файл заказчика 23.09.2026:
 * «документы по площадке можно свернуть», «при нажатии на документ должно появиться окно с
 * реквизитами документа»). Свёрнутые площадки запоминаются в браузере — у каждого свои.
 */
import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  ChevronRight,
  EyeOff,
  FileUp,
  Paperclip,
  Pencil,
  Plus,
  ShoppingCart,
  X,
} from 'lucide-react'
import type {
  EcoCatalogItem,
  EcoOverview,
  EcoScopeApi,
  EcoSection,
  EcoSite,
  EcoSiteItem,
  EcoStatusColor,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  ECO_FILE_ACCEPT,
  ECO_STATUS_COLORS,
  ecoDocumentTermText,
  ecoReportDueText,
  ecoReportPeriodText,
  ecoSectionKind,
  ecoSiteCategoryLabel,
  ecoSourceLabel,
  formatEcoDate,
  groupEcoItemsBySite,
  sortEcoReports,
} from '@/lib/ecology'
import { cn } from '@/lib/utils'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { StatusDot } from '@/design-system/components/primitives/status-dot'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableGroupRow,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { ItemDialog } from './item-dialog'
import { SiteDialog } from './site-dialog'
import type { EcoOrderStartInput } from './order/start-order'

const ALL_SITES = 'all'

/** Подписи фильтров — как в макете варианта Б. */
const COLOR_LABELS: Record<EcoSection, Record<EcoStatusColor, string>> = {
  projects: {
    red: 'Просрочено',
    amber: 'Истекает / актуализация',
    green: 'Действует',
    gray: 'Не требуется',
  },
  reports: {
    red: 'Просрочено',
    amber: 'Скоро срок',
    green: 'Сдано / ведётся',
    gray: 'Не требуется',
  },
  lab: {
    red: 'Просрочено',
    amber: 'Скоро срок',
    green: 'Выполнено',
    gray: 'Не требуется',
  },
}

/** Тексты подраздела: кнопки, пустое состояние и пояснение про площадку без категории. */
const SECTION_TEXT: Record<
  EcoSection,
  { add: string; order: string; empty: string; unknown: string }
> = {
  projects: {
    add: 'Внести имеющийся документ',
    order: 'Заказать проект',
    empty: 'Документов пока нет.',
    unknown: '',
  },
  reports: {
    add: 'Отметить сданный отчёт',
    order: 'Заказать сдачу отчётности',
    empty: 'Отчётности пока нет.',
    unknown: 'нужная отчётность определится после',
  },
  lab: {
    add: 'Отметить выполненный контроль',
    order: 'Заказать лабораторный контроль',
    empty: 'Лабораторного контроля пока нет.',
    unknown: 'нужный лабораторный контроль определится после',
  },
}

function FilterChip({
  label,
  count,
  color,
  active,
  onClick,
}: {
  label: string
  count?: number
  color?: EcoStatusColor
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-2 whitespace-nowrap rounded-pill border px-3 py-1.5 text-13 font-medium transition-colors',
        active
          ? 'border-accent bg-accent-bg text-accent-txt'
          : 'border-line bg-white text-txt-secondary hover:bg-hover',
      )}
    >
      {color ? <StatusDot variant={color} label={label} aria-hidden /> : null}
      {label}
      {count !== undefined ? <span className="font-semibold text-txt">{count}</span> : null}
    </button>
  )
}

/**
 * Подпись под названием строки: происхождение, дата, числа карточки и примечания. Из позиции
 * справочника `position` — подпись даты документа (у постановки на учёт — дата актуализации
 * сведений в реестре) и числовые поля: значение строки этой площадки, например «кол-во источников
 * выбросов, шт.: 15» (ecology-sources-by-site); у «нет, нужен» поле скрыто — и в подписи его нет.
 */
function sourceLine(item: EcoSiteItem, position?: EcoCatalogItem): string {
  const developedLabel = position?.fieldLabels.developed_on
  const parts = [ecoSourceLabel(item.source)]
  if (item.pendingOrderNumber)
    parts.push(
      `заказ ${item.pendingOrderNumber}${item.pendingStage ? ` · этап «${item.pendingStage}»` : ''}`,
    )
  if (item.kind === 'document' && item.developedOn) {
    const date = formatEcoDate(item.developedOn)
    parts.push(developedLabel ? `${developedLabel.toLowerCase()}: ${date}` : `разработан ${date}`)
  }
  for (const f of position?.fields ?? []) {
    const value = item.values[f.key]
    if (f.type === 'number' && value && item.presence === 'present')
      parts.push(`${f.label.toLowerCase()}: ${value}`)
  }
  if (item.docNumber) parts.push(item.docNumber)
  if (item.needsUpdateReason) parts.push(item.needsUpdateReason)
  if (item.note) parts.push(item.note)
  return parts.join(' · ')
}

type DialogState =
  | { open: false }
  | { open: true; item: EcoSiteItem | null; siteId: string | null; readOnly?: boolean }

/** Свёрнутые площадки запоминаются между заходами — это удобство просмотра, а не данные. */
const COLLAPSED_KEY = 'eco:collapsed-sites'

function readCollapsedSites(): Set<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY)
    const ids: unknown = raw ? JSON.parse(raw) : []
    return new Set(
      Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [],
    )
  } catch {
    return new Set()
  }
}

function writeCollapsedSites(ids: Set<string>): void {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...ids]))
  } catch {
    // приватный режим или запрет на хранилище — сворачивание просто не переживёт перезагрузку
  }
}

/** Действия строки: скан (скачать / прикрепить / убрать), правка, «убрать из раздела». */
function ItemActions({
  item,
  editable,
  busy,
  onPickScan,
  onDeleteScan,
  onEdit,
  onDismiss,
  onOrder,
  onOpenOrder,
}: {
  item: EcoSiteItem
  editable: boolean
  busy: boolean
  onPickScan: () => void
  onDeleteScan: () => void
  onEdit: () => void
  onDismiss: () => void
  /** Заказать услугу по этой строке (ecology-order); у команды не показывается. */
  onOrder?: () => void
  /** Открыть заказ, который уже идёт по этой строке. */
  onOpenOrder?: () => void
}) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      {onOpenOrder ? (
        <Button size="sm" variant="secondary" className="whitespace-nowrap" onClick={onOpenOrder}>
          Открыть заказ
        </Button>
      ) : null}
      {onOrder ? (
        <Button
          size="sm"
          className="whitespace-nowrap"
          onClick={onOrder}
          aria-label={`Заказать «${item.name}»`}
        >
          Заказать
        </Button>
      ) : null}
      {item.fileUrl ? (
        <a
          href={item.fileUrl}
          className="inline-flex max-w-40 items-center gap-1 text-13 text-accent hover:underline"
          title={item.fileName}
        >
          <Paperclip className="size-4 shrink-0 stroke-current" aria-hidden />
          <span className="truncate">{item.fileName}</span>
        </a>
      ) : null}
      {editable ? (
        <>
          {item.fileUrl ? (
            <Button
              size="sm"
              variant="secondary"
              title="Убрать скан"
              aria-label={`Убрать скан «${item.name}»`}
              disabled={busy}
              onClick={onDeleteScan}
            >
              <X className="size-4 stroke-current" aria-hidden />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              title="Прикрепить скан"
              aria-label={`Прикрепить скан «${item.name}»`}
              disabled={busy}
              onClick={onPickScan}
            >
              <FileUp className="size-4 stroke-current" aria-hidden />
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            title="Изменить"
            aria-label={`Изменить «${item.name}»`}
            disabled={busy}
            onClick={onEdit}
          >
            <Pencil className="size-4 stroke-current" aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="secondary"
            title="Убрать из раздела"
            aria-label={`Убрать «${item.name}» из раздела`}
            disabled={busy}
            onClick={onDismiss}
          >
            <EyeOff className="size-4 stroke-current" aria-hidden />
          </Button>
        </>
      ) : null}
    </div>
  )
}

function NameCell({ item, position }: { item: EcoSiteItem; position?: EcoCatalogItem }) {
  return (
    <DataTableCell className="min-w-56">
      <div className="font-medium text-txt">{item.name}</div>
      <div className="mt-0.5 text-12 text-txt-tertiary">{sourceLine(item, position)}</div>
    </DataTableCell>
  )
}

function StatusCell({ item }: { item: EcoSiteItem }) {
  return (
    <DataTableCell>
      <Badge variant={item.status.color} className="whitespace-nowrap">
        {item.status.label}
      </Badge>
    </DataTableCell>
  )
}

function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="border-b border-line p-4 text-txt-secondary">
        {children}
      </td>
    </tr>
  )
}

const LINK_BUTTON = 'font-medium text-accent hover:underline'

/**
 * Строку стоит заказать: просрочено, отсутствует или требует актуализации (ТЗ §3.5 — клик по такой
 * строке ведёт в заказ). «Не требуется» и действующие документы заказывать не предлагаем.
 */
function needsOrder(item: EcoSiteItem): boolean {
  return item.status.color === 'red' || item.status.color === 'amber'
}

export function ItemsSection({
  section,
  api,
  overview,
  catalog,
  byTeam = false,
  onChange,
  onSurvey,
  onOrder,
  onOpenOrder,
}: {
  /** Подраздел: «Проектные работы», «Отчётность» или «Лабораторный контроль». */
  section: EcoSection
  api: EcoScopeApi
  overview: EcoOverview
  catalog: EcoCatalogItem[]
  /** Команда Центрики: правит и строки, выполненные Центрикой. */
  byTeam?: boolean
  onChange: (overview: EcoOverview) => void
  /** Открыть опрос по площадке (ecology-survey). */
  onSurvey: (site: EcoSite) => void
  /**
   * Заказать услугу по строке или по разделу (ecology-order); у команды не показывается — заказ
   * оформляет клиент. Кнопка раздела передаёт подраздел — мастер покажет только его услуги
   * (ecology-order-service-search).
   */
  onOrder?: (input: EcoOrderStartInput) => void
  /** Открыть заказ, который идёт по строке («На согласовании»). */
  onOpenOrder?: (orderId: string) => void
}) {
  const [color, setColor] = useState<EcoStatusColor | null>(null)
  const [siteFilter, setSiteFilter] = useState(ALL_SITES)
  const [dialog, setDialog] = useState<DialogState>({ open: false })
  const [siteDialog, setSiteDialog] = useState<EcoSite | null>(null)
  const [confirmDismiss, setConfirmDismiss] = useState<EcoSiteItem | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsedSites)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const uploadFor = useRef<EcoSiteItem | null>(null)

  const kind = ecoSectionKind(section)
  const isDocument = kind === 'document'
  const text = SECTION_TEXT[section]
  const multiSite = overview.sites.length > 1
  // Фильтр по площадке мог остаться от удалённой площадки — тогда показываем все.
  const siteId = overview.sites.some((s) => s.id === siteFilter) ? siteFilter : ALL_SITES
  const sites = useMemo(
    () => overview.sites.filter((s) => siteId === ALL_SITES || s.id === siteId),
    [overview.sites, siteId],
  )
  // Счётчики фильтров — по тем же строкам, что показывает таблица: с выбранной площадкой счётчик
  // «всех площадок» обещал строки, которых в таблице нет.
  const counts = useMemo(() => {
    const out: Record<EcoStatusColor, number> = { red: 0, amber: 0, green: 0, gray: 0 }
    const visible = new Set(sites.map((s) => s.id))
    for (const item of overview.items) {
      if (item.section === section && visible.has(item.siteId)) out[item.status.color] += 1
    }
    return out
  }, [sites, overview.items, section])
  // Позиции справочника строк — подпись даты и числа карточки в подписи строки (ecology-card-fields).
  const positions = useMemo(() => new Map(catalog.map((c) => [c.id, c])), [catalog])
  // Позиции справочника, которые Центрика берёт в заказ: по остальным строках кнопки «Заказать» нет
  // — мастер заказа их не предлагает, а подача такого заказа отклоняется.
  const orderableIds = useMemo(
    () => new Set(catalog.filter((c) => c.showInOrder).map((c) => c.id)),
    [catalog],
  )
  const groups = useMemo(
    () =>
      groupEcoItemsBySite(sites, overview.items, 'projects')
        .map((g) => ({
          ...g,
          items: color === null ? g.items : g.items.filter((i) => i.status.color === color),
        }))
        // С активным фильтром по цвету площадки без подходящих строк не показываем.
        .filter((g) => color === null || g.items.length > 0),
    [sites, overview.items, color],
  )
  const reports = useMemo(
    () =>
      sortEcoReports(sites, overview.items, section === 'lab' ? 'lab' : 'reports').filter(
        (i) => color === null || i.status.color === color,
      ),
    [sites, overview.items, color, section],
  )
  // Площадки без категории и без строк подраздела — пояснение под таблицей.
  const reportlessUnknown = sites.filter(
    (s) =>
      s.category === '' && !overview.items.some((i) => i.siteId === s.id && i.section === section),
  )

  // Строка карточки — из свежего обзора: файлы поля, прикреплённые в открытой карточке, видны сразу.
  const dialogItem =
    dialog.open && dialog.item
      ? (overview.items.find((i) => i.id === dialog.item?.id) ?? dialog.item)
      : null

  const addLabel = text.add
  const canEdit = (item: EcoSiteItem) => byTeam || item.source !== 'centrika'
  const openAdd = (site: string | null) => setDialog({ open: true, item: null, siteId: site })
  /** Карточка строки: правка — кому можно, остальным просмотр (строку заполнила Центрика). */
  const openItem = (item: EcoSiteItem) =>
    setDialog({ open: true, item, siteId: item.siteId, readOnly: !canEdit(item) })
  const toggleSite = (siteId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(siteId)) next.delete(siteId)
      else next.add(siteId)
      writeCollapsedSites(next)
      return next
    })

  async function run(item: EcoSiteItem, action: () => Promise<EcoOverview>, fallback: string) {
    setError(null)
    setBusyId(item.id)
    try {
      onChange(await action())
    } catch (err) {
      setError(getErrorMessage(err, fallback))
    } finally {
      setBusyId(null)
    }
  }

  // Клик по действиям строки не должен открывать её карточку (клик по самой строке — открывает).
  const actionsFor = (item: EcoSiteItem) => (
    <DataTableCell className="text-right" onClick={(e) => e.stopPropagation()}>
      <ItemActions
        item={item}
        editable={canEdit(item)}
        busy={busyId === item.id}
        onPickScan={() => {
          uploadFor.current = item
          fileRef.current?.click()
        }}
        onDeleteScan={() =>
          void run(item, () => api.deleteItemFile(item.id), 'Не удалось убрать скан')
        }
        onEdit={() => openItem(item)}
        onDismiss={() => setConfirmDismiss(item)}
        onOrder={
          onOrder &&
          !item.pendingOrderId &&
          needsOrder(item) &&
          orderableIds.has(item.catalogItemId)
            ? () => onOrder({ catalogItemId: item.catalogItemId, siteIds: [item.siteId] })
            : undefined
        }
        onOpenOrder={
          item.pendingOrderId && onOpenOrder ? () => onOpenOrder(item.pendingOrderId) : undefined
        }
      />
    </DataTableCell>
  )

  const documentsColumns = 4
  const reportsColumns = multiSite ? 6 : 5

  return (
    <div>
      <Toolbar aria-label="Фильтры">
        <FilterChip label="Все" active={color === null} onClick={() => setColor(null)} />
        {ECO_STATUS_COLORS.filter((c) => c !== 'gray' || counts.gray > 0).map((c) => (
          <FilterChip
            key={c}
            label={COLOR_LABELS[section][c]}
            count={counts[c]}
            color={c}
            active={color === c}
            onClick={() => setColor((cur) => (cur === c ? null : c))}
          />
        ))}
        <div className="flex flex-wrap items-center gap-2.5 nw:ml-auto">
          {multiSite ? (
            <Select value={siteId} onValueChange={setSiteFilter}>
              <SelectTrigger className="w-64 max-w-full text-left" aria-label="Площадка">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SITES}>Все площадки</SelectItem>
                {overview.sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Button
            variant="secondary"
            onClick={() => openAdd(siteId === ALL_SITES ? null : siteId)}
            leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
          >
            {addLabel}
          </Button>
          {onOrder ? (
            <Button
              onClick={() =>
                onOrder({ section, siteIds: siteId === ALL_SITES ? undefined : [siteId] })
              }
              leftIcon={<ShoppingCart className="size-4 stroke-current" aria-hidden />}
            >
              {text.order}
            </Button>
          ) : null}
        </div>
      </Toolbar>

      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}

      <Panel>
        <div className="overflow-x-auto">
          {isDocument ? (
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeaderCell>Документ</DataTableHeaderCell>
                  <DataTableHeaderCell>Срок действия</DataTableHeaderCell>
                  <DataTableHeaderCell>Статус</DataTableHeaderCell>
                  <DataTableHeaderCell>{''}</DataTableHeaderCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {groups.length === 0 ? (
                  <EmptyRow colSpan={documentsColumns}>По выбранному фильтру строк нет</EmptyRow>
                ) : null}
                {groups.map(({ site, items }) => (
                  <Fragment key={site.id}>
                    <DataTableGroupRow colSpan={documentsColumns} className="p-0">
                      <button
                        type="button"
                        onClick={() => toggleSite(site.id)}
                        aria-expanded={!collapsed.has(site.id)}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-hover"
                      >
                        <ChevronRight
                          className={cn(
                            'size-4 shrink-0 stroke-current transition-transform',
                            !collapsed.has(site.id) && 'rotate-90',
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          {site.name} · {ecoSiteCategoryLabel(site.category)}
                        </span>
                        <span className="shrink-0 font-normal normal-case tracking-normal text-txt-tertiary">
                          {items.length === 0 ? 'нет строк' : `строк: ${items.length}`}
                        </span>
                      </button>
                    </DataTableGroupRow>
                    {collapsed.has(site.id) ? null : items.length === 0 ? (
                      <EmptyRow colSpan={documentsColumns}>
                        {site.category === '' ? (
                          <>
                            Категория площадки не указана. Чтобы узнать категорию и какие документы
                            нужны площадке,{' '}
                            <button
                              type="button"
                              className={LINK_BUTTON}
                              onClick={() => onSurvey(site)}
                            >
                              пройдите опрос по площадке
                            </button>
                            ; если категория известна —{' '}
                            <button
                              type="button"
                              className={LINK_BUTTON}
                              onClick={() => setSiteDialog(site)}
                            >
                              укажите её
                            </button>
                            . Имеющиеся документы можно{' '}
                            <button
                              type="button"
                              className={LINK_BUTTON}
                              onClick={() => openAdd(site.id)}
                            >
                              внести сразу
                            </button>
                            .
                          </>
                        ) : (
                          <>
                            Документов пока нет.{' '}
                            <button
                              type="button"
                              className={LINK_BUTTON}
                              onClick={() => openAdd(site.id)}
                            >
                              {addLabel}
                            </button>
                          </>
                        )}
                      </EmptyRow>
                    ) : null}
                    {collapsed.has(site.id)
                      ? null
                      : items.map((item) => (
                          <DataTableRow key={item.id} clickable onClick={() => openItem(item)}>
                            <NameCell item={item} position={positions.get(item.catalogItemId)} />
                            <DataTableCell className="text-txt-secondary">
                              {ecoDocumentTermText(item)}
                            </DataTableCell>
                            <StatusCell item={item} />
                            {actionsFor(item)}
                          </DataTableRow>
                        ))}
                  </Fragment>
                ))}
              </DataTableBody>
            </DataTable>
          ) : (
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeaderCell>Отчёт / мероприятие</DataTableHeaderCell>
                  {multiSite ? <DataTableHeaderCell>Площадка</DataTableHeaderCell> : null}
                  <DataTableHeaderCell>Периодичность</DataTableHeaderCell>
                  <DataTableHeaderCell>Ближайший срок</DataTableHeaderCell>
                  <DataTableHeaderCell>Статус</DataTableHeaderCell>
                  <DataTableHeaderCell>{''}</DataTableHeaderCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {reports.length === 0 ? (
                  <EmptyRow colSpan={reportsColumns}>
                    {color !== null ? (
                      'По выбранному фильтру строк нет'
                    ) : (
                      <>
                        {text.empty}{' '}
                        <button
                          type="button"
                          className={LINK_BUTTON}
                          onClick={() => openAdd(siteId === ALL_SITES ? null : siteId)}
                        >
                          {addLabel}
                        </button>
                      </>
                    )}
                  </EmptyRow>
                ) : null}
                {reports.map((item) => (
                  <DataTableRow key={item.id} clickable onClick={() => openItem(item)}>
                    <NameCell item={item} position={positions.get(item.catalogItemId)} />
                    {multiSite ? (
                      <DataTableCell className="text-txt-secondary">
                        {overview.sites.find((s) => s.id === item.siteId)?.name}
                      </DataTableCell>
                    ) : null}
                    <DataTableCell className="text-txt-secondary">
                      {ecoReportPeriodText(item)}
                    </DataTableCell>
                    <DataTableCell className="text-txt-secondary">
                      {ecoReportDueText(item)}
                    </DataTableCell>
                    <StatusCell item={item} />
                    {actionsFor(item)}
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
          )}
        </div>
      </Panel>

      {!isDocument && reportlessUnknown.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1 text-13 text-txt-secondary">
          {reportlessUnknown.map((s) => (
            <li key={s.id}>
              {s.name}: категория не указана — {text.unknown}{' '}
              <button type="button" className={LINK_BUTTON} onClick={() => onSurvey(s)}>
                опроса по площадке
              </button>
              .
            </li>
          ))}
        </ul>
      ) : null}

      <input
        ref={fileRef}
        type="file"
        accept={ECO_FILE_ACCEPT}
        className="hidden"
        aria-label="Файл скана"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          const item = uploadFor.current
          uploadFor.current = null
          if (file && item)
            void run(item, () => api.uploadItemFile(item.id, file), 'Не удалось прикрепить скан')
        }}
      />

      <ItemDialog
        open={dialog.open}
        section={section}
        item={dialogItem}
        catalog={catalog}
        sites={overview.sites}
        items={overview.items}
        defaultSiteId={dialog.open ? dialog.siteId : null}
        readOnly={dialog.open ? dialog.readOnly : false}
        api={api}
        onClose={() => setDialog({ open: false })}
        onSaved={(next) => {
          onChange(next)
          setDialog({ open: false })
        }}
        onOverviewChange={onChange}
      />

      <SiteDialog
        open={siteDialog !== null}
        site={siteDialog}
        api={api}
        onClose={() => setSiteDialog(null)}
        onSaved={(next) => {
          onChange(next)
          setSiteDialog(null)
        }}
      />

      <ConfirmDialog
        open={confirmDismiss !== null}
        title="Убрать из раздела"
        message={
          confirmDismiss
            ? `Убрать «${confirmDismiss.name}» из раздела? Вернуть строку можно, внеся данные заново.`
            : ''
        }
        onClose={() => setConfirmDismiss(null)}
        buttons={[
          {
            label: 'Убрать',
            variant: 'danger',
            onClick: () => {
              const item = confirmDismiss
              setConfirmDismiss(null)
              if (item) void run(item, () => api.dismissItem(item.id), 'Не удалось убрать строку')
            },
          },
          { label: 'Отмена', variant: 'secondary', onClick: () => setConfirmDismiss(null) },
        ]}
      />
    </div>
  )
}
